import React, { useCallback, useState } from 'react';

import { useLocation } from '@reach/router';
import { Link } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';

import { useMixpanel } from '../functions/Mixpanel';

import { FooterModal } from './FooterModal';
import {
  Company,
  FooterLink,
  Legal,
  Patients,
  Resources,
  Socials,
  Solutions,
} from './footerData';
import './global.css';

type FooterColumnProps = {
  data: FooterLink[];
  title: string;
  callback?: (text: string) => void;
};

const FooterColumn = (props: FooterColumnProps) => {
  return (
    <div className={columnClassNames.container}>
      <h2 className={columnClassNames.title}>{props.title}</h2>
      {props.data.map((item, index) => {
        return (
          <h2 key={index} className={columnClassNames.link}>
            <Link to={item.path} onClick={() => props.callback?.(item.title)}>
              {item.title}
            </Link>
          </h2>
        );
      })}
    </div>
  );
};

const columnClassNames = {
  container: ['tw-flex', 'tw-w-fit', 'tw-flex-col', 'tw-gap-[8px]'].join(' '),
  title: ['tw-body2-emphasis', 'tw-text-dusk'].join(' '),
  link: ['tw-body2', 'tw-text-white'].join(' '),
};

const Footer = ({ pageName }: { pageName?: string }) => {
  const [modal, setModal] = useState(false);

  const { mixpanel } = useMixpanel();
  const url = useLocation();

  const trackFooter = useCallback(
    (title: string) => {
      mixpanel.track('Navigate Footer', { Link: title });
    },
    [mixpanel],
  );

  const trackSignUp = useCallback(() => {
    mixpanel.track('Newsletter SignUp Success', {
      Page: pageName || url.pathname,
    });
  }, [mixpanel, pageName, url.pathname]);

  return (
    <div className={classNames.background}>
      <div className={classNames.container}>
        <div className={classNames.logosContainer}>
          <StaticImage
            className={classNames.logo}
            src="../images/logoAlternate.png"
            alt="Alife logo"
            quality={100}
            objectFit="contain"
          />
          <div className={classNames.complianceContainer}>
            <StaticImage
              className={classNames.logo}
              src="../images/HIPAA.png"
              alt="Hipaa logo"
              quality={100}
              objectFit="contain"
            />
            <StaticImage
              className={classNames.logo}
              src="../images/soc.png"
              alt="SOC logo"
              quality={100}
              objectFit="contain"
            />
          </div>
        </div>
        <div className={classNames.contentContainer}>
          <div className={classNames.linksContainer}>
            <FooterColumn
              data={Company}
              title="Company"
              callback={trackFooter}
            />
            <FooterColumn
              data={Solutions}
              title="Solutions"
              callback={trackFooter}
            />
            <FooterColumn
              data={Resources}
              title="Resources"
              callback={trackFooter}
            />{' '}
            <FooterColumn
              data={Patients}
              title="For Patients"
              callback={trackFooter}
            />
          </div>
          <div className={classNames.subscribeContainer}>
            <h2 className={classNames.subscribeText}>
              Sign up for our newsletter for the latest advancements in
              fertility care.
            </h2>
            <button
              className={classNames.subscribeButton}
              onClick={() => setModal(true)}
            >
              Subscribe
            </button>
          </div>
        </div>
        <div className={classNames.bottomContainer}>
          <div className={classNames.legalContainer}>
            <p className={classNames.copyRight}>&copy;2024 by Alife Health</p>
            {Legal.map((value, i) => (
              <Link
                key={i}
                to={value.path}
                onClick={() => trackFooter(value.title)}
              >
                <p className={classNames.legal}>{value.title}</p>
              </Link>
            ))}
          </div>
          <div className={classNames.socialContainer}>
            {Socials.map((value, i) => (
              <Link key={i} to={value.url}>
                <img className={classNames.social} src={value.icon} />
              </Link>
            ))}
          </div>
        </div>
      </div>
      {modal && (
        <FooterModal
          onClose={() => setModal(false)}
          onSuccess={() => trackSignUp()}
        />
      )}
    </div>
  );
};

const classNames = {
  background: [
    'tw-flex',
    'tw-self-stretch',
    'tw-justify-center',
    'tw-bg-space',
  ].join(' '),
  container: [
    'tw-flex',
    'tw-flex-1',
    'tw-flex-col',
    'tw-gap-[32px]',
    'tw-my-[24px]',
    'tw-mx-[16px]',
    'md:tw-mx-[22px]',
    'xl:tw-mx-0',
    'xl:tw-max-w-[1128px]',
  ].join(' '),
  logosContainer: ['tw-flex', 'tw-justify-between', 'tw-self-stretch'].join(
    ' ',
  ),
  logo: ['tw-flex', 'tw-w-[60px]', 'tw-h-[60px]'].join(' '),
  complianceContainer: ['tw-flex', 'tw-gap-[4px]'].join(' '),
  contentContainer: [
    'tw-flex',
    'tw-flex-col',
    'tw-gap-[32px]',
    'xl:tw-flex-row',
    'xl:tw-justify-between',
  ].join(' '),
  linksContainer: [
    'tw-grid',
    'tw-w-fit',
    'tw-grid-cols-2',
    'md:tw-grid-cols-4',
    'tw-gap-x-[16px]',
    'md:tw-gap-x-[32px]',
    'tw-gap-y-[32px]',
  ].join(' '),
  subscribeContainer: [
    'tw-flex',
    'tw-flex-col',
    'tw-gap-[16px]',
    'xl:tw-items-end',
  ].join(' '),
  subscribeText: [
    'tw-body1',
    'tw-text-white',
    'xl:tw-text-right',
    'tw-max-w-[360px]',
  ].join(' '),
  subscribeButton: [
    'tw-flex',
    'tw-w-fit',
    'tw-px-[12px]',
    'tw-py-[2px]',
    'tw-bg-white',
    'tw-rounded-[52px]',
    'tw-justify-center',
  ].join(' '),
  bottomContainer: [
    'tw-flex',
    'tw-flex-col',
    'md:tw-flex-row',
    'md:tw-justify-between',
    'tw-border-t-[1px]',
    'tw-border-white',
    'tw-pt-[16px]',
  ].join(' '),
  legalContainer: [
    'tw-grid',
    'tw-grid-cols-2',
    'md:tw-grid-flow-col',
    'tw-gap-[12px]',
    'md:tw-gap-[20px]',
  ].join(' '),
  copyRight: [
    'tw-caption',
    'tw-text-white',
    'tw-col-[1/-1]',
    'md:tw-col-1',
  ].join(' '),
  legal: ['tw-caption', 'tw-text-white'].join(' '),
  socialContainer: [
    'tw-flex',
    'tw-gap-[16px]',
    'tw-mt-[32px]',
    'md:tw-mt-0',
  ].join(' '),
  social: [
    'tw-h-[32px]',
    'tw-w-[32px]',
    'sm:tw-h-[18px]',
    'sm:tw-w-[18px]',
    'tw-object-contain',
  ].join(' '),
};

export default Footer;
